/**
 * 校徽订购 - 产品详情预设模版（HTML）。最后更新：2026-06-09
 */
import { BaseApiUrl } from '../../api/base';/** 产品说明文案 HTML 预设下拉选项（与「样式模板」无关） */
export const productDetailTemplateOptions = [
  { key: '', label: '自定义（不载入预设）' },
  { key: 'intro', label: '完整办理须知' },
  { key: 'simple', label: '精简办理须知' }
]

/** 产品详情内置示例图（相对 API 站点路径） */
export const productDetailSampleImagePath = '/badge-order/product-sample.png'

/** 示例图默认展示尺寸（与原图 1172×435 比例一致） */
export const productDetailSampleImageWidth = 400
export const productDetailSampleImageHeight = 148

/** 示例图展示样式（编辑器与家长端 H5 一致；窄屏按宽度等比缩小） */
export const productDetailSampleImageStyle =
  'width:' +
  productDetailSampleImageWidth +
  'px;max-width:100%;height:auto;display:block;margin:0 auto;border:1px solid #ebeef5;border-radius:4px;'

const oldProductDetailSampleImagePattern = /product-detail-sample\.png/g

function migrateProductDetailSampleImagePath(html) {
  if (!html) return html
  return html.replace(oldProductDetailSampleImagePattern, 'product-sample.png')
}

const sampleImgHtml =
  '<p style="text-align:center;margin:12px 0;">' +
  '<img src="' +
  productDetailSampleImagePath +
  '" alt="校徽样式示例" width="' +
  productDetailSampleImageWidth +
  '" height="' +
  productDetailSampleImageHeight +
  '" style="' +
  productDetailSampleImageStyle +
  '" />' +
  '</p>' +
  '<p style="font-size:12px;color:#909399;text-align:center;margin:0 0 8px;">上图仅为样式参考；办理页将按您填写的信息生成专属效果图</p>'

/** 默认示例图 HTML（带 API 完整地址，供编辑器显示） */
export function getProductDetailSampleImageHtml(apiBase) {
  return resolveProductDetailHtmlImages(sampleImgHtml, apiBase)
}

/**
 * 向已有产品详情插入/更新默认示例图（兼容旧版纯文字模版）
 * @param {string} html
 * @param {string} [apiBase]
 * @returns {string}
 */
export function insertProductDetailSampleImage(html, apiBase) {
  const block = getProductDetailSampleImageHtml(apiBase)
  let content = migrateProductDetailSampleImagePath(html || '')
  content = content.replace(/<p>[\s\S]*?您也可在下方插入[\s\S]*?<\/p>/gi, '')
  if (content.indexOf('product-sample') >= 0) {
    return normalizeProductDetailSampleImageSize(resolveProductDetailHtmlImages(content, apiBase))
  }
  if (content.indexOf('<h2>3、制作时间</h2>') >= 0) {
    return content.replace('<h2>3、制作时间</h2>', block + '<h2>3、制作时间</h2>')
  }
  if (content.indexOf('2、校徽样式') >= 0 || content.indexOf('校徽样式如下') >= 0) {
    return content + block
  }
  return content + block
}

/** 是否缺少内置示例图 */
export function needsProductDetailSample(html) {
  if (!html) return false
  const content = migrateProductDetailSampleImagePath(html)
  if (content.indexOf('product-sample') >= 0) return false
  return (
    content.indexOf('2、校徽样式') >= 0 ||
    content.indexOf('校徽样式如下') >= 0 ||
    content.indexOf('您也可在下方插入') >= 0
  )
}

/** 将已插入的示例图统一为默认 400×148 展示尺寸 */
export function normalizeProductDetailSampleImageSize(html) {
  const content = migrateProductDetailSampleImagePath(html)
  if (!content || content.indexOf('product-sample') < 0) return content
  return content.replace(/<img\b([^>]*\bproduct-sample[^>]*)>/gi, (tag, attrs) => {
    let next = attrs
      .replace(/\s(width|height)=["'][^"']*["']/gi, '')
      .replace(/\sstyle=["'][^"']*["']/gi, '')
    return (
      '<img' +
      next +
      ' width="' +
      productDetailSampleImageWidth +
      '" height="' +
      productDetailSampleImageHeight +
      '" style="' +
      productDetailSampleImageStyle +
      '" />'
    )
  })
}

/** 加载/展示前：补示例图 + 统一尺寸 + 解析图片完整 URL */
export function normalizeProductDetailHtml(html, apiBase) {
  let content = migrateProductDetailSampleImagePath(html || '')
  if (needsProductDetailSample(content)) {
    content = insertProductDetailSampleImage(content, apiBase)
  } else {
    content = resolveProductDetailHtmlImages(content, apiBase)
  }
  content = normalizeProductDetailSampleImageSize(content)
  return content
}

const templates = {
  intro: `
<h2>1、校徽信息确认</h2>
<p>办理前请务必确认学生的<strong>年级、班级、姓名</strong>是否正确。提交前可再次修改；一旦提交进入制作流程，信息将无法更改。</p>
<h2>2、校徽样式如下</h2>
<p>家长在<strong>办理页填写姓名、班级、证件照等信息后，系统会按所选套餐自动生成校徽效果图</strong>。效果图含学生姓名、照片、校徽编号及德育二维码等，提交前请仔细核对。</p>
${sampleImgHtml}
<h2>3、制作时间</h2>
<p>校徽均为<strong>定制制作</strong>，制作/补办周期较长，约<strong>20个工作日</strong>。补办费用约<strong>20元/枚</strong>，费用由第三方平台代收。</p>
<p>建议家长适量订购备用，避免校徽丢失影响考勤推送。</p>
<h2>4、补卡与丢失</h2>
<p>如校徽遗失或损坏，可通过本入口重新提交校徽制作信息办理补办。</p>
<h2>5、校徽作用</h2>
<p><strong>a. 考勤推送：</strong>到校/离校自动记录，并向家长推送签到时间及现场照片、短视频。</p>
<p><strong>b. 在校联系：</strong>学生可在学校指定区域通过校徽联系家长。</p>
<p><strong>c. 德育评分：</strong>家长扫描校徽二维码可查看学生德育成长档案。</p>
`.trim(),
  simple: `
<h2>办理须知</h2>
<p>1. 请核对学生姓名、班级等信息，提交制作后不可修改。</p>
<p>2. 校徽为定制产品，约 20 个工作日完成制作。</p>
<p>3. 如有疑问请联系学校或客服。</p>
`.trim()
}

/**
 * 将模版中的相对图片路径转为可访问的完整地址（管理端 Quill / 家长端展示）
 * @param {string} html
 * @param {string} [apiBase]
 * @returns {string}
 */
export function resolveProductDetailHtmlImages(html, apiBase) {
  if (!html) return html
  const base = (apiBase || BaseApiUrl || '').replace(/\/$/, '')
  if (!base) return html
  return html.replace(/src="\/badge-order\//g, 'src="' + base + '/badge-order/')
}

/** 保存前将完整图片地址还原为相对路径，便于多环境部署 */
export function stripProductDetailHtmlImages(html, apiBase) {
  if (!html) return html
  const base = (apiBase || BaseApiUrl || '').replace(/\/$/, '')
  if (!base) return html
  const escaped = base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return html.replace(new RegExp('src="' + escaped + '/badge-order/', 'g'), 'src="/badge-order/')
}

/**
 * 根据模版 key 获取 HTML（图片地址已带 API 前缀，便于编辑器直接显示）
 * @param {string} key
 * @returns {string}
 */
export function getProductDetailTemplateHtml(key) {
  const html = templates[key] || ''
  return resolveProductDetailHtmlImages(html, BaseApiUrl)
}

/** 将 HTML 转为可比较的纯文本（忽略标签与空白差异） */
function toComparableText(html, apiBase) {
  const normalized = normalizeProductDetailHtml(html || '', apiBase)
  const relative = stripProductDetailHtmlImages(normalized, apiBase)
  return relative.replace(/<[^>]+>/g, '').replace(/\s+/g, '').trim()
}

/**
 * 根据已保存的产品详情 HTML 反推当前使用的预设模版 key（无匹配则返回空字符串表示自定义）
 * @param {string} html
 * @param {string} [apiBase]
 * @returns {string}
 */
export function resolveProductDetailTemplateKey(html, apiBase) {
  const current = toComparableText(html, apiBase)
  if (!current) return ''

  for (const opt of productDetailTemplateOptions) {
    if (!opt.key) continue
    const tplHtml = templates[opt.key]
    if (!tplHtml) continue
    if (current === toComparableText(tplHtml, apiBase)) return opt.key
  }

  // 用户可能在预设基础上微调，用特征段落做宽松匹配
  if (current.indexOf('校徽信息确认') >= 0 && current.indexOf('校徽作用') >= 0) return 'intro'
  if (current.indexOf('办理须知') >= 0 && current.indexOf('校徽信息确认') < 0) return 'simple'
  return ''
}
